<script lang="ts">
	import { createEventDispatcher, setContext } from 'svelte';
	import { writable } from 'svelte/store';
	import {
		Button,
		Modal,
		Wizard,
		WizardContainer,
		WizardContent,
		WizardItem,
		toast,
	} from '@Shelly/shelly-design-system';
	import { format } from '../../../services/i18n';
	import { edit } from 'src/stores/ui/edit_lists';
	import SelectRoomStep from './Steps/SelectRoomStep.svelte';
	import ModeStep from '../TempSchemeWizard/Steps/ModeStep.svelte';
	import ControledStep from '../TempSchemeWizard/Steps/ControledStep.svelte';
	import SensorStep from '../TempSchemeWizard/Steps/SensorStep.svelte';
	import SchedulesStep from '../TempSchemeWizard/Steps/SchedulesStep.svelte';
	import DetailsStep from './Steps/DetailsStep.svelte';

	import {
		scrollToElement,
		setWizardSteps,
		updateWizardStepOnPrevNext,
	} from '../../Lists/list-helpers';
	import { capFirstChar, _deepClone, deepCopy } from '../../../helpers/utilities';
	import { postReq } from '../../../services/http';
	import { scenes } from 'src/stores/lists/scenes';
	import { temp_scheme_prototype } from './temp_scheme_object';
	import type { ITempSchemeObj } from '../../../types/TemperatureSchemeType';
	import ExitConfirmationModal from 'src/components/ExitConfirmationModal.svelte';
	import { temp_schemes } from 'src/stores/lists/scenes-derived';
	import { celsius_to_fahrenheit, fahrenheit_to_celsius } from 'src/helpers/interface_helpers';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { onItemSaved, orderItemsInRoom } from 'src/helpers/add-entity-helpers';
	import type { ISceneObj } from 'src/types/SceneType';

	export let id,
		showAddEditModal = false,
		temp_scheme_object: ITempSchemeObj;

	let diffLoad = false;
	const dispatch = createEventDispatcher();
	const deftemp = $temp_schemes[temp_scheme_object.id]?.deftemp;
	$: if (id && !diffLoad && temp_scheme_object.deftemp !== deftemp) {
		temp_scheme_object.deftemp = deftemp;
		diffLoad = true;
	}

	let allRelaysRefs: { [key: string | number]: HTMLElement } = {};
	let firstSelectedRef: HTMLElement = null;
	let selectedRelay;
	let wizardItemsContainerRef: HTMLElement = null;
	let modalRef: Modal = null;
	let selectedRoomRef: HTMLElement = null;
	let scrolledToView = false;
	let errorsSetupStep = false;

	let containersRefsObj = writable({
		scrollContainerRef: null,
		wizardContainerRef: null,
	});

	setContext('thermostatWizardContainers', containersRefsObj);
	const clonedThermostat = deepCopy(temp_scheme_object);
	let showExitConfirmationModal = false;
	let exitConfirmationModalState = false;
	const keyPress = (e) => {
		e.stopImmediatePropagation();
	};
	$: if (showAddEditModal) {
		window.addEventListener('keydown', keyPress);
	} else {
		window.removeEventListener('keydown', keyPress);
	}

	let removeContainerEventListener = () => {};

	const scrollSelectedCardToView = () => {
		removeContainerEventListener();

		scrolledToView = false;
		let elementRef = null;
		let wizardStepIndex = 0;
		if (activeStep.activeIndex === 0 && selectedRoomRef && !scrolledToView) {
			elementRef = selectedRoomRef;
			wizardStepIndex = 0;
		} else if (activeStep.activeIndex === 2 && allRelaysRefs[selectedRelay?.id] && !scrolledToView) {
			wizardStepIndex = 2;
			console.log('all relays ', allRelaysRefs);
			console.log('selected relay ', selectedRelay);
			elementRef = allRelaysRefs[selectedRelay.id];
		} else if (activeStep.activeIndex === 3 && firstSelectedRef && !scrolledToView) {
			wizardStepIndex = 3;

			elementRef = firstSelectedRef;
		}

		if (!elementRef) {
			modalRef.scrollToTop();
		} else {
			console.log('element ref ', elementRef);
			removeContainerEventListener = scrollToElement(
				elementRef,
				wizardItemsContainerRef,
				activeStep.activeIndex,
				wizardStepIndex,
			);
		}

		scrolledToView = true;
	};

	let wizardItems = setWizardSteps([
		capFirstChar($format('select_room_step', { default: 'select room' })), // room,
		capFirstChar($format('set_up_step', { default: 'set up' })), // heat or cool mode, units, threshold
		capFirstChar($format('type_actuators', { default: 'actuators' })), // select controled devices
		capFirstChar($format('sensor_step', { default: 'sensor' })), // sensor selection
		capFirstChar($format('schedules_step', { default: 'schedules' })), // base emperature and custom intervals
		capFirstChar($format('details_step', { default: 'details' })), // name
	]);

	let activeStep = wizardItems[0];

	const previousButton = $format('button_back', { default: 'Back' });

	$: isFirstStep = activeStep.activeIndex === 0;
	$: isLastStepWizard = activeStep.activeIndex === wizardItems.length - 1;

	$: nextButton = isLastStepWizard
		? $format('button_save', { default: 'Save' })
		: $format('button_next', { default: 'Next' });

	$: disabled = disabledConditions[activeStep.activeIndex];

	let disabledConditions = [];
	$: disabledConditions = [
		temp_scheme_object?._meta?.room === '', // 1
		temp_scheme_object?._meta?.type === '' || errorsSetupStep, // 2
		temp_scheme_object?.tocool?.blk?.length <= 0 ||
			temp_scheme_object?.toheat?.blk?.length <= 0 ||
			errorsSetupStep, // 3

		temp_scheme_object?.tocool?.blk?.length <= 0 ||
			temp_scheme_object?.toheat?.blk?.length <= 0 ||
			!temp_scheme_object?.tschem?.r?.match(/dev:/) ||
			errorsSetupStep, // 4

		temp_scheme_object?.intervals?.length < 0 ||
			temp_scheme_object?._meta?.room === '' ||
			temp_scheme_object?._meta?.type === '' ||
			errorsSetupStep ||
			temp_scheme_object?.tocool?.blk?.length <= 0 ||
			temp_scheme_object?.toheat?.blk?.length <= 0 ||
			!temp_scheme_object?.tschem?.r?.match(/dev:/), // 5

		typeof temp_scheme_object?._meta?.name === 'undefined' ||
			temp_scheme_object?._meta?.name?.trim() === '', // 6
	];

	const onClickWizardItem = (e, index: number) => {
		wizardItems = wizardItems.map((item, i) => {
			if (i === index) {
				activeStep = {
					...item,
					activeIndex: i,
				};

				return {
					...item,
					active: true,
				};
			}

			return {
				...item,
				active: false,
			};
		});

		scrollSelectedCardToView();
	};

	const isNewOrRoomChanged = (clonedThermostat: ISceneObj, thermostat: ISceneObj): boolean => {
		const existing = $temp_schemes[clonedThermostat?._id];
		return !existing || Number(existing?._meta?.room) !== Number(thermostat?._meta?.room);
	};

	const save_scheme = async (scheme) => {
		let res: any = {};
		scheme._meta.name = scheme._meta.name.trim();
		scheme.ths = Number(scheme.ths).toFixed(2);
		if (scheme?.temp_fav_positions) {
			scheme.temp_fav_positions = scheme.temp_fav_positions.map((pos) => ({
				...pos,
				...($temp_schemes[id]?.isinfh !== scheme?.isinfh && {
					value: scheme?.isinfh
						? celsius_to_fahrenheit(pos.value)
						: +fahrenheit_to_celsius(pos.value).toFixed(1),
				}),
				active: false,
			}));
		}

		if (isNewOrRoomChanged(clonedThermostat, scheme)) {
			await orderItemsInRoom(scheme, 'thermostats');
			scheme._meta.position = 0; // New/changed room  items should be added at the first position to eliminate the need to scroll when adding a new item
		}

		if (id && id !== '') {
			res = await postReq('/scene/edit', {
				id: scheme._id,
				scene_script: JSON.stringify(scheme),
			});
			if (res.isok && res?.data?.scene_id) {
				// console.log(scene);
				return scheme;
			}
		} else {
			//we save new scene
			res = await postReq('/scene/add', {
				scene_script: JSON.stringify(scheme),
			});
			// console.log('Spazz res is ', res);
			if (res.isok && res?.data?.scene_id) {
				scheme._id = res.data.scene_id;
				scheme = {
					...scheme,
					_meta: {
						...(scheme._meta ?? {}),
						new: true, // When adding a new item, set the temporary prop `new` to show the pulse animation
					},
				};
				return scheme;
			}
		}
	};

	const handleSave = () => {
		//if temp_scheme_object is valid
		save_scheme(temp_scheme_object)
			.then((res) => {
				if (id && id !== '') {
					scenes.editScene(res);
				} else {
					scenes.addScene(res);

					if (!$edit) {
						onItemSaved(res, 'thermostats');
					}
				}
			})
			.catch(() => {
				toast.open({ type: 'danger', title: 'Unable to save temp scheme' });
			});
	};

	const clearData = () => {
		temp_scheme_object = _deepClone(temp_scheme_prototype) as ITempSchemeObj;
		id = '';
		console.log(temp_scheme_object);
	};

	const onCloseEditModal = () => {
		// wizardItems = resetWizardItems(wizardItems);
		// activeStep = wizardItems[0];
		clearData();
		showAddEditModal = false;
		edit.set(false);
		dispatch('close');
	};

	const onFinishCardEdit = async () => {
		await handleSave();
		onCloseEditModal();
	};

	const onClickPrevNextButton = (type: 'prev' | 'next') => {
		const result = updateWizardStepOnPrevNext(type, wizardItems, activeStep);
		wizardItems = result.wizardItems;
		activeStep = { ...result.activeWizardStep };

		console.log('prev next scroll call ');
		scrollSelectedCardToView();
	};

	const onSelectRoom = (event) => {
		temp_scheme_object = {
			...temp_scheme_object,
			_meta: {
				...temp_scheme_object._meta,
				room: event.detail,
			},
		};
	};
</script>

<Modal
	on:close={() => {
		if (JSON.stringify(temp_scheme_object) !== JSON.stringify(clonedThermostat)) {
			showExitConfirmationModal = true;
			exitConfirmationModalState = false;
		} else {
			showAddEditModal = false;
			onCloseEditModal();
		}
	}}
	showModal={showAddEditModal}
	title={temp_scheme_object._id
		? $format('prompt_title_edit_thermostat', {
				default: 'Edit thermostat',
			})
		: $format('prompt_title_add_thermostat', {
				default: 'Add thermostat',
			})}
	bind:this={modalRef}
	bind:scrollContainerRef={$containersRefsObj.scrollContainerRef}
	removeScrollPadding
>
	<Wizard slot="toolbar">
		{#each wizardItems as item, i (i)}
			<WizardItem
				on:click={(e) => {
					onClickWizardItem(e, i);
				}}
				active={item.active}
				title={item.title}
				disabled={disabledConditions[i - 1]}
				showMoreDots={item.showMoreDots}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>

	<div slot="content">
		<WizardContainer
			bind:wizardItemsContainerRef
			activeStepIndex={activeStep.activeIndex}
			adaptiveHeight
			bind:wizardContainerRef={$containersRefsObj.wizardContainerRef}
		>
			{#each wizardItems as { }, i (i)}
				<WizardContent
					active={activeStep.activeIndex === i}
					class={classMap({
						'pl-0 pr-0': true,
						'pt-s': i === 1 || i === 4 || i === 5, // DO NOT REMOVE COMMENT: Padding references (additional: doing this for consistant spacing)
					})}
				>
					{#if i === 0}
						<SelectRoomStep
							bind:temp_scheme_object
							bind:selectedRef={selectedRoomRef}
							on:selectRoom={onSelectRoom}
						/>
					{:else if i === 1}
						<ModeStep bind:temp_scheme_object bind:errorsSetupStep />
					{:else if i === 2}
						<ControledStep bind:temp_scheme_object bind:allRelaysRefs bind:selectedRelay />
					{:else if i === 3}
						<SensorStep bind:temp_scheme_object bind:firstSelectedRef />
					{:else if i === 4}
						<SchedulesStep bind:temp_scheme_object />
					{:else if i === 5}
						<DetailsStep bind:temp_scheme_object />
					{/if}
				</WizardContent>
			{/each}
		</WizardContainer>
	</div>

	<div slot="footer" class="d-flex justify-content-end">
		{#if !isFirstStep}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				icon="fas fa-chevron-left"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => {
					onClickPrevNextButton('prev');
				}}
			>
				{previousButton}
			</Button>
		{/if}
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			icon={isLastStepWizard ? 'fas fa-save' : ''}
			rightIcon={isLastStepWizard ? '' : 'fas fa-chevron-right'}
			{disabled}
			responsiveSize={{
				sm: 'm',
			}}
			on:click={() => {
				if (isLastStepWizard) {
					onFinishCardEdit();
				} else {
					onClickPrevNextButton('next');
				}
			}}
		>
			{nextButton}
		</Button>
	</div>
</Modal>

<ExitConfirmationModal
	type={$format('the_thermostat', { default: 'the thermostat' })}
	{exitConfirmationModalState}
	{showExitConfirmationModal}
	{showAddEditModal}
	on:close={(e) => {
		exitConfirmationModalState = e.detail.exitConfirmationModalState;
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
	}}
	on:confirm={(e) => {
		dispatch('close');
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
		showAddEditModal = e.detail.showAddEditModal;
	}}
/>
